import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { api } from "../../services/api";

// ── Types ─────────────────────────────────────────────────────────────────────

interface PoolTotal {
  participantId: string;
  win: number;
  place: number;
  show: number;
}

interface GuestBet {
  id: string;
  bettorName: string;
  participantName: string;
  winAmount: number;
  placeAmount: number;
  showAmount: number;
  voided: boolean;
}

interface PublicPmData {
  eventName: string;
  status: "pending" | "open" | "locked" | "settled";
  participants: { id: string; name: string; scratched?: boolean }[];
  minBet?: number;
  maxBet?: number;
  showAllBets?: boolean;
  poolTotals: PoolTotal[];
  results?: {
    winParticipantId: string;
    placeParticipantId: string;
    showParticipantId: string;
  };
  payoutSummary?: {
    bettorName: string;
    total: number;
    lines: {
      participantName: string;
      winPayout: number;
      placePayout: number;
      showPayout: number;
      lineTotal: number;
      isBlindDraw?: boolean;
    }[];
  }[];
}

interface BetRow {
  key: string;
  participantId: string;
  winAmt: string;
  placeAmt: string;
  showAmt: string;
}

function newRow(): BetRow {
  return {
    key: `${Date.now()}-${Math.random()}`,
    participantId: "",
    winAmt: "",
    placeAmt: "",
    showAmt: "",
  };
}

/** Returns the device ID for this share code, creating one if needed. */
function getDeviceId(shareCode: string): string {
  const key = `guestDeviceId_${shareCode}`;
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

// ── Shared header ─────────────────────────────────────────────────────────────

function GuestHeader({
  eventName,
  minBet,
  maxBet,
}: {
  eventName?: string;
  minBet?: number;
  maxBet?: number;
}) {
  return (
    <div className='bg-[#102232] text-white px-4 py-4'>
      <div className='flex items-center gap-2 mb-3'>
        <img
          src='/logo.png'
          alt='Golf Kraken'
          className='h-6 w-6 rounded-md object-contain'
        />
        <span className='text-xs font-bold text-white/70 uppercase tracking-widest'>
          Golf Kraken
        </span>
      </div>
      {eventName && (
        <>
          <p className='text-[11px] font-semibold text-white/50 uppercase tracking-widest'>
            Betting Slip
          </p>
          <h1 className='text-lg font-bold mt-0.5'>{eventName}</h1>
          {(minBet != null || maxBet != null) && (
            <p className='text-xs text-white/60 mt-1'>
              {minBet != null && `Min $${minBet}`}
              {minBet != null && maxBet != null && " · "}
              {maxBet != null && `Max $${maxBet}`}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ── Tab bar ───────────────────────────────────────────────────────────────────

type Tab = "slip" | "bets" | "results";

function TabBar({
  active,
  onChange,
  betsLabel = "My Bets",
}: {
  active: Tab;
  onChange: (t: Tab) => void;
  betsLabel?: string;
}) {
  const tabs: { id: Tab; label: string }[] = [
    { id: "slip", label: "Bet Slip" },
    { id: "bets", label: betsLabel },
    { id: "results", label: "Results" },
  ];
  return (
    <div className='flex border-b border-gray-200 bg-white sticky top-0 z-10'>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={[
            "flex-1 py-3 text-sm font-semibold transition-colors",
            active === t.id
              ? "text-amber-600 border-b-2 border-amber-500"
              : "text-gray-500 hover:text-gray-700",
          ].join(" ")}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Field Coverage table ──────────────────────────────────────────────────────

function FieldCoverage({
  participants,
  poolTotals,
}: {
  participants: PublicPmData["participants"];
  poolTotals: PoolTotal[];
}) {
  if (participants.length === 0) return null;
  return (
    <div className='rounded-2xl border border-slate-200 bg-slate-50 shadow-sm p-4'>
      <p className='text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2'>
        Field Coverage
      </p>
      <table className='w-full text-xs'>
        <thead>
          <tr className='border-b border-gray-100'>
            <th className='text-left py-1 font-semibold text-gray-400 pr-3'>
              Horse
            </th>
            <th className='text-right py-1 font-semibold text-gray-400 w-14 px-2'>
              Win
            </th>
            <th className='text-right py-1 font-semibold text-gray-400 w-14 px-2'>
              Pl
            </th>
            <th className='text-right py-1 font-semibold text-gray-400 w-14 pl-2'>
              Sh
            </th>
          </tr>
        </thead>
        <tbody>
          {participants.map((p) => {
            const t = poolTotals.find((x) => x.participantId === p.id);
            const win = t?.win ?? 0;
            const place = t?.place ?? 0;
            const show = t?.show ?? 0;
            const hasBets = win + place + show > 0;
            return (
              <tr
                key={p.id}
                className={[
                  "border-b border-gray-50 last:border-0",
                  p.scratched ? "opacity-40" : "",
                ].join(" ")}
              >
                <td
                  className={[
                    "py-1.5 pr-2 font-medium truncate",
                    hasBets ? "text-gray-800" : "text-gray-400",
                    p.scratched ? "line-through" : "",
                  ].join(" ")}
                  style={{ maxWidth: "1px", width: "100%" }}
                >
                  <span className='block truncate'>{p.name}</span>
                </td>
                <td
                  className={[
                    "py-1.5 text-right tabular-nums w-14 px-2",
                    win > 0 ? "text-gray-700 font-semibold" : "text-gray-300",
                  ].join(" ")}
                >
                  {win > 0 ? `$${win}` : "—"}
                </td>
                <td
                  className={[
                    "py-1.5 text-right tabular-nums w-14 px-2",
                    place > 0 ? "text-gray-700 font-semibold" : "text-gray-300",
                  ].join(" ")}
                >
                  {place > 0 ? `$${place}` : "—"}
                </td>
                <td
                  className={[
                    "py-1.5 text-right tabular-nums w-14 pl-2",
                    show > 0 ? "text-gray-700 font-semibold" : "text-gray-300",
                  ].join(" ")}
                >
                  {show > 0 ? `$${show}` : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function GuestJoinPage() {
  const { shareCode } = useParams<{ shareCode: string }>();

  const [pm, setPm] = useState<PublicPmData | null>(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>("slip");

  // Bet slip state
  const [bettorName, setBettorName] = useState(
    () => localStorage.getItem("guestBettorName") ?? "",
  );
  const [rows, setRows] = useState<BetRow[]>([newRow()]);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [betRejected, setBetRejected] = useState(false);

  // My bets state
  const [myBets, setMyBets] = useState<GuestBet[]>([]);
  const [betsLoading, setBetsLoading] = useState(false);
  const [betsFilter, setBetsFilter] = useState("");

  // Results refresh state
  const [refreshing, setRefreshing] = useState(false);

  // Payout breakdown expand state
  const [expandedBettors, setExpandedBettors] = useState<Set<string>>(
    new Set(),
  );

  function toggleBettor(name: string) {
    setExpandedBettors((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  const deviceId = shareCode ? getDeviceId(shareCode) : "";

  // Load event on mount
  useEffect(() => {
    if (!shareCode) return;
    api.join
      .getEvent(shareCode)
      .then((res) => setPm((res as any).data as PublicPmData))
      .catch((err: any) => setLoadError(err.message ?? "Event not found"))
      .finally(() => setLoading(false));
  }, [shareCode]);

  // Load bets when Bets tab is activated
  useEffect(() => {
    if (activeTab !== "bets" || !shareCode) return;
    loadMyBets();
  }, [activeTab, pm?.showAllBets]);

  function loadMyBets() {
    if (!shareCode) return;
    setBetsLoading(true);
    const request = pm?.showAllBets
      ? api.join.getAllBets(shareCode)
      : deviceId
        ? api.join.getMyBets(shareCode, deviceId)
        : Promise.resolve({ data: [] });
    request
      .then((res) => setMyBets((res as any).data as GuestBet[]))
      .catch(() => {})
      .finally(() => setBetsLoading(false));
  }

  function refreshEvent() {
    if (!shareCode || refreshing) return;
    setRefreshing(true);
    api.join
      .getEvent(shareCode)
      .then((res) => setPm((res as any).data as PublicPmData))
      .catch(() => {})
      .finally(() => setRefreshing(false));
  }

  const activeParticipants = pm?.participants.filter((p) => !p.scratched) ?? [];

  function updateRow(
    key: string,
    field: keyof Omit<BetRow, "key">,
    value: string,
  ) {
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)),
    );
  }

  function removeRow(key: string) {
    setRows((prev) =>
      prev.length > 1 ? prev.filter((r) => r.key !== key) : prev,
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError("");

    const name = bettorName.trim();
    if (!name) {
      setSubmitError("Please enter your name");
      return;
    }

    const bets = rows
      .filter((r) => r.participantId)
      .map((r) => ({
        participantId: r.participantId,
        winAmount: Number(r.winAmt) || 0,
        placeAmount: Number(r.placeAmt) || 0,
        showAmount: Number(r.showAmt) || 0,
      }))
      .filter((b) => b.winAmount + b.placeAmount + b.showAmount > 0);

    if (bets.length === 0) {
      setSubmitError("Add at least one bet with an amount");
      return;
    }

    setSubmitting(true);
    try {
      localStorage.setItem("guestBettorName", name);
      await api.join.submitBets(shareCode!, {
        bettorName: name,
        deviceId,
        bets,
      });
      setSubmitSuccess(true);
      setRows([newRow()]);
      // Refresh my bets in background
      if (shareCode && deviceId) {
        api.join
          .getMyBets(shareCode, deviceId)
          .then((res) => setMyBets((res as any).data as GuestBet[]))
          .catch(() => {});
      }
    } catch (err: any) {
      const msg: string = err.message ?? "";
      if (
        msg.toLowerCase().includes("closed") ||
        msg.toLowerCase().includes("locked")
      ) {
        setBetRejected(true);
        setPm((prev) => (prev ? { ...prev, status: "locked" } : prev));
      } else {
        setSubmitError(msg || "Failed to submit bets");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const filteredBets = useMemo(
    () =>
      betsFilter.trim()
        ? myBets.filter((b) =>
            b.participantName.toLowerCase().includes(betsFilter.toLowerCase()),
          )
        : myBets,
    [myBets, betsFilter],
  );

  // ── Loading / error states ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <p className='text-gray-400 text-sm'>Loading…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center p-4'>
        <div className='text-center'>
          <p className='text-3xl mb-2'>❌</p>
          <p className='text-red-500 text-sm'>{loadError}</p>
        </div>
      </div>
    );
  }

  if (!pm) return null;

  const participantById = new Map(pm.participants.map((p) => [p.id, p]));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className='min-h-screen bg-gray-50'>
      <GuestHeader
        eventName={pm.eventName}
        minBet={pm.minBet}
        maxBet={pm.maxBet}
      />
      <TabBar
        active={activeTab}
        onChange={setActiveTab}
        betsLabel={pm?.showAllBets ? "All Bets" : "My Bets"}
      />

      {/* ── BET SLIP TAB ─────────────────────────────────────────────────── */}
      {activeTab === "slip" && (
        <div className='px-4 py-5 space-y-4 max-w-lg mx-auto'>
          {pm.status === "pending" ? (
            <div className='flex flex-col items-center py-12 text-center'>
              <p className='text-4xl mb-3'>⏳</p>
              <p className='font-semibold text-gray-700'>
                Betting hasn’t opened yet
              </p>
              <p className='text-xs text-gray-400 mt-2'>
                The host will open betting shortly.
              </p>
              <button
                onClick={refreshEvent}
                disabled={refreshing}
                className='mt-5 px-4 py-2 rounded-xl bg-[#102232] text-white text-sm font-semibold hover:bg-[#1a3448] transition-colors disabled:opacity-50'
              >
                {refreshing ? "Checking…" : "🔄 Check Again"}
              </button>
            </div>
          ) : pm.status !== "open" ? (
            <div className='flex flex-col items-center py-12 text-center'>
              <p className='text-4xl mb-3'>🔒</p>
              <p className='font-semibold text-gray-700'>Betting is closed</p>
              <p className='text-xs text-gray-400 mt-2'>
                Results are being tallied. Check the Results tab soon!
              </p>
              {betRejected && (
                <div className='mt-5 max-w-xs bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-left'>
                  <p className='text-sm font-semibold text-red-700'>
                    Your bet was not saved
                  </p>
                  <p className='text-xs text-red-500 mt-1'>
                    Betting closed before your submission went through. Contact
                    the host to add your bet manually.
                  </p>
                </div>
              )}
            </div>
          ) : submitSuccess ? (
            <div className='flex flex-col items-center py-12 text-center'>
              <p className='text-5xl mb-3'>🎲</p>
              <h2 className='text-xl font-bold text-gray-800 mb-1'>
                You&rsquo;re in!
              </h2>
              <p className='text-sm text-gray-500 mb-5'>
                Your bets have been submitted. The host will confirm payment.
              </p>
              <button
                onClick={() => setSubmitSuccess(false)}
                className='text-sm text-amber-600 hover:text-amber-700 font-semibold underline'
              >
                Submit more bets
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className='space-y-4'>
              <div>
                <label className='block text-xs font-semibold text-gray-500 mb-1'>
                  Your Name
                </label>
                <input
                  value={bettorName}
                  onChange={(e) => setBettorName(e.target.value)}
                  placeholder='Enter your name'
                  required
                  className='w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400'
                />
              </div>

              <div className='space-y-3'>
                {rows.map((row, i) => (
                  <div
                    key={row.key}
                    className='bg-white rounded-2xl border border-gray-100 shadow-sm p-3 space-y-2'
                  >
                    <div className='flex items-center justify-between'>
                      <span className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide'>
                        Bet {i + 1}
                      </span>
                      {rows.length > 1 && (
                        <button
                          type='button'
                          onClick={() => removeRow(row.key)}
                          className='text-red-300 hover:text-red-500 p-1'
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                    <select
                      value={row.participantId}
                      onChange={(e) =>
                        updateRow(row.key, "participantId", e.target.value)
                      }
                      required
                      className='w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white'
                    >
                      <option value=''>Select participant…</option>
                      {activeParticipants.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <div className='grid grid-cols-3 gap-2'>
                      {(
                        [
                          ["winAmt", "Win $"],
                          ["placeAmt", "Place $"],
                          ["showAmt", "Show $"],
                        ] as [keyof Omit<BetRow, "key">, string][]
                      ).map(([field, label]) => (
                        <div key={field}>
                          <label className='block text-[10px] font-semibold text-gray-400 mb-1'>
                            {label}
                          </label>
                          <input
                            type='number'
                            min='0'
                            step='1'
                            placeholder='0'
                            value={row[field]}
                            onChange={(e) =>
                              updateRow(
                                row.key,
                                field,
                                e.target.value.replace(/[^0-9]/g, ""),
                              )
                            }
                            className='w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-400'
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <button
                type='button'
                onClick={() => setRows((prev) => [...prev, newRow()])}
                className='w-full py-2 border border-dashed border-amber-300 rounded-xl text-sm text-amber-600 hover:bg-amber-50 font-medium flex items-center justify-center gap-1.5 transition-colors'
              >
                <Plus size={14} />
                Add another horse
              </button>

              {submitError && (
                <p className='text-xs text-red-500'>{submitError}</p>
              )}

              <button
                type='submit'
                disabled={submitting}
                className='w-full py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm transition-colors disabled:opacity-50'
              >
                {submitting ? "Submitting…" : "🎲 Submit Bets"}
              </button>

              <p className='text-[11px] text-gray-400 text-center pb-2'>
                Bets are pending until the host confirms payment.
              </p>
            </form>
          )}

          <FieldCoverage
            participants={pm.participants}
            poolTotals={pm.poolTotals}
          />
        </div>
      )}

      {/* ── MY BETS TAB ──────────────────────────────────────────────────── */}
      {activeTab === "bets" && (
        <div className='px-4 py-5 max-w-lg mx-auto space-y-4'>
          {betsLoading ? (
            <p className='text-center text-sm text-gray-400 py-10'>Loading…</p>
          ) : myBets.length === 0 ? (
            <div className='text-center py-10 text-gray-400 text-sm'>
              <p className='text-2xl mb-2'>🎲</p>
              <p>
                {pm?.showAllBets
                  ? "No bets yet."
                  : "No bets from this device yet."}
              </p>
            </div>
          ) : (
            <>
              <input
                className='w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400'
                placeholder='Filter by horse / participant…'
                value={betsFilter}
                onChange={(e) => setBetsFilter(e.target.value)}
              />
              {filteredBets.length === 0 ? (
                <p className='text-center text-sm text-gray-400 py-6'>
                  No bets match that filter.
                </p>
              ) : (
                <div className='bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden'>
                  <table className='w-full text-xs'>
                    <thead className='bg-gray-50 border-b border-gray-100'>
                      <tr className='text-gray-400'>
                        <th className='text-left px-4 py-2 font-semibold'>
                          Name
                        </th>
                        <th className='text-left px-2 py-2 font-semibold'>
                          Participant
                        </th>
                        <th className='text-right px-2 py-2 font-semibold'>
                          Win
                        </th>
                        <th className='text-right px-2 py-2 font-semibold'>
                          Pl
                        </th>
                        <th className='text-right px-3 py-2 font-semibold'>
                          Sh
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBets.map((b) => (
                        <React.Fragment key={b.id}>
                          <tr
                            className={[
                              "border-b border-gray-50",
                              b.voided ? "bg-red-50" : "",
                            ].join(" ")}
                          >
                            <td
                              className={[
                                "px-4 py-2.5 font-semibold",
                                b.voided
                                  ? "text-gray-400 line-through"
                                  : "text-gray-800",
                              ].join(" ")}
                            >
                              {b.bettorName}
                            </td>
                            <td
                              className={[
                                "px-2 py-2.5",
                                b.voided
                                  ? "text-gray-400 line-through"
                                  : "text-gray-600",
                              ].join(" ")}
                            >
                              {b.participantName}
                            </td>
                            <td
                              className={[
                                "px-2 py-2.5 text-right tabular-nums",
                                b.voided ? "text-gray-400 line-through" : "",
                              ].join(" ")}
                            >
                              {b.winAmount > 0 ? `$${b.winAmount}` : "—"}
                            </td>
                            <td
                              className={[
                                "px-2 py-2.5 text-right tabular-nums",
                                b.voided ? "text-gray-400 line-through" : "",
                              ].join(" ")}
                            >
                              {b.placeAmount > 0 ? `$${b.placeAmount}` : "—"}
                            </td>
                            <td
                              className={[
                                "px-3 py-2.5 text-right tabular-nums",
                                b.voided ? "text-gray-400 line-through" : "",
                              ].join(" ")}
                            >
                              {b.showAmount > 0 ? `$${b.showAmount}` : "—"}
                            </td>
                          </tr>
                          {b.voided && (
                            <tr className='bg-red-50 border-b border-gray-50'>
                              <td
                                colSpan={5}
                                className='px-4 pb-2 text-[11px] text-red-500'
                              >
                                ⚠️ Voided — payment was not confirmed before
                                betting closed
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── RESULTS TAB ──────────────────────────────────────────────────── */}
      {activeTab === "results" && (
        <div className='px-4 py-5 max-w-lg mx-auto space-y-5'>
          {pm.status === "settled" && pm.results ? (
            <>
              <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-4'>
                <p className='text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3'>
                  Final Finishing Order
                </p>
                {[
                  { label: "🥇 Win", id: pm.results.winParticipantId },
                  { label: "🥈 Place", id: pm.results.placeParticipantId },
                  { label: "🥉 Show", id: pm.results.showParticipantId },
                ].map(({ label, id }) => (
                  <div
                    key={label}
                    className='flex items-center justify-between py-2 border-b border-gray-100 last:border-0'
                  >
                    <span className='text-sm text-gray-500'>{label}</span>
                    <span className='font-bold text-gray-800'>
                      {participantById.get(id)?.name ?? id}
                    </span>
                  </div>
                ))}
              </div>

              {pm.payoutSummary && pm.payoutSummary.length > 0 && (
                <div className='bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden'>
                  <div className='px-4 py-2.5 bg-green-600'>
                    <p className='text-xs font-bold text-white uppercase tracking-wide'>
                      Payouts
                    </p>
                  </div>
                  <table className='w-full text-sm'>
                    <tbody>
                      {pm.payoutSummary.map((row) => {
                        const isExpanded = expandedBettors.has(row.bettorName);
                        return (
                          <React.Fragment key={row.bettorName}>
                            <tr
                              className='border-b border-gray-100 cursor-pointer hover:bg-gray-50'
                              onClick={() => toggleBettor(row.bettorName)}
                            >
                              <td className='px-4 py-2.5 font-semibold text-gray-800'>
                                <span className='inline-flex items-center gap-1.5'>
                                  <span className='text-gray-400 text-[10px] w-3'>
                                    {isExpanded ? "▲" : "▼"}
                                  </span>
                                  {row.bettorName}
                                </span>
                              </td>
                              <td className='px-4 py-2.5 text-right font-bold text-green-700'>
                                ${row.total.toFixed(2)}
                              </td>
                            </tr>
                            {isExpanded &&
                              row.lines.map((line, i) => (
                                <tr
                                  key={i}
                                  className='bg-green-50 border-b border-green-100'
                                >
                                  <td className='pl-8 pr-3 py-1.5 text-[11px] text-gray-600'>
                                    {line.isBlindDraw
                                      ? "🎲 Blind Draw"
                                      : line.participantName}
                                  </td>
                                  <td className='px-4 py-1.5 text-right text-[11px] font-semibold text-green-700'>
                                    {line.isBlindDraw
                                      ? `$${line.lineTotal.toFixed(2)}`
                                      : [
                                          line.winPayout > 0
                                            ? `Win $${line.winPayout.toFixed(2)}`
                                            : null,
                                          line.placePayout > 0
                                            ? `Pl $${line.placePayout.toFixed(2)}`
                                            : null,
                                          line.showPayout > 0
                                            ? `Sh $${line.showPayout.toFixed(2)}`
                                            : null,
                                        ]
                                          .filter(Boolean)
                                          .join(" + ")}
                                  </td>
                                </tr>
                              ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div className='flex flex-col items-center py-12 text-center'>
              <p className='text-4xl mb-3'>⏳</p>
              <p className='font-semibold text-gray-700'>
                Results are being tallied
              </p>
              <p className='text-xs text-gray-400 mt-2'>
                Check back once the host has settled the event.
              </p>
              <button
                onClick={refreshEvent}
                disabled={refreshing}
                className='mt-5 px-4 py-2 rounded-xl bg-[#102232] text-white text-sm font-semibold hover:bg-[#1a3448] transition-colors disabled:opacity-50'
              >
                {refreshing ? "Checking…" : "🔄 Refresh Results"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
